/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

/**
 * Dynamic Inputs Form — renders a minimal JSON Schema as inputs.
 *
 * Why custom instead of react-jsonschema-form: the 6 starter workflows
 * use a tight subset (string / textarea / number / boolean / enum) and
 * keeping the renderer small avoids a ~120kB dep just to render five
 * field types. We can swap to rjsf later if schemas grow nested or
 * conditional. For now this covers everything ADR-018 §11 specifies.
 *
 * Supported types:
 *   - string                              → text input
 *   - string + format:"textarea"          → textarea
 *   - string + maxLength > 200            → textarea (auto)
 *   - string + enum                       → dropdown
 *   - number / integer                    → number input (min/max honored)
 *   - boolean                             → toggle switch
 *
 * Not supported (return null + console warn so the page can render
 * something instead of crashing):
 *   - nested objects, arrays, oneOf/anyOf, conditional schemas
 *
 * Validation: required fields are tracked; submit is disabled until all
 * required ones have a value. We do NOT do full JSON Schema validation
 * because V2 does — the failing path is V2 returning 400 which the
 * caller surfaces. We just guard against obviously broken submissions.
 */

import React, { useState } from 'react';

function isLongText(field) {
  if (field?.format === 'textarea') return true;
  if (typeof field?.maxLength === 'number' && field.maxLength > 200) return true;
  return false;
}

function StringField({ name, field, value, onChange, required }) {
  const placeholder = field.description || field.title || name;
  if (Array.isArray(field.enum) && field.enum.length > 0) {
    return (
      <select
        id={`wf-input-${name}`}
        className="wf-form-input"
        value={value ?? ''}
        onChange={(e) => onChange(name, e.target.value)}
        required={required}
      >
        <option value="">{`— Select ${field.title || name} —`}</option>
        {field.enum.map((opt) => (
          <option key={String(opt)} value={String(opt)}>{String(opt)}</option>
        ))}
      </select>
    );
  }
  if (isLongText(field)) {
    return (
      <textarea
        id={`wf-input-${name}`}
        className="wf-form-input wf-form-textarea"
        value={value ?? ''}
        rows={6}
        onChange={(e) => onChange(name, e.target.value)}
        placeholder={placeholder}
        maxLength={field.maxLength || undefined}
        required={required}
      />
    );
  }
  return (
    <input
      id={`wf-input-${name}`}
      type="text"
      className="wf-form-input"
      value={value ?? ''}
      onChange={(e) => onChange(name, e.target.value)}
      placeholder={placeholder}
      maxLength={field.maxLength || undefined}
      required={required}
    />
  );
}

function NumberField({ name, field, value, onChange, required }) {
  return (
    <input
      id={`wf-input-${name}`}
      type="number"
      className="wf-form-input"
      value={value ?? ''}
      onChange={(e) => {
        const v = e.target.value;
        onChange(name, v === '' ? null : Number(v));
      }}
      min={field.minimum}
      max={field.maximum}
      step={field.type === 'integer' ? 1 : 'any'}
      placeholder={field.description || name}
      required={required}
    />
  );
}

function BooleanField({ name, value, onChange }) {
  const checked = Boolean(value);
  return (
    <label htmlFor={`wf-input-${name}`} className="wf-form-switch">
      <input
        id={`wf-input-${name}`}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(name, e.target.checked)}
      />
      <span className="wf-form-switch-track" aria-hidden="true">
        <span className="wf-form-switch-thumb" />
      </span>
      <span className="wf-form-switch-label">{checked ? 'On' : 'Off'}</span>
    </label>
  );
}

// JSON textarea for free-form object inputs (e.g. wms_stakeholder_debate's
// warehouse_context). Holds raw text locally; pushes a PARSED object to the
// parent's `values[name]` so JSON.stringify({inputs}) ships a real object on
// submit. Empty / invalid JSON ⇒ onChange(name, undefined), so the required-
// gate at `:165` treats the field as not-yet-provided. Mirrors the
// JSON.parse + inline-error pattern from SpineSeeds.jsx's parseSeedJson().
function ObjectField({ name, field, value, onChange, required }) {
  const [raw, setRaw] = useState(() => {
    if (value && typeof value === 'object') return JSON.stringify(value, null, 2);
    if (typeof value === 'string') return value;
    return '';
  });
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const text = e.target.value;
    setRaw(text);
    if (text.trim() === '') {
      setError(null);
      onChange(name, undefined);
      return;
    }
    try {
      const parsed = JSON.parse(text);
      setError(null);
      onChange(name, parsed);
    } catch (err) {
      setError(`Invalid JSON: ${err.message}`);
      onChange(name, undefined);
    }
  };

  return (
    <>
      <textarea
        id={`wf-input-${name}`}
        className="wf-form-input wf-form-textarea"
        value={raw}
        rows={6}
        onChange={handleChange}
        placeholder={field.description || `JSON object for ${name}`}
        required={required}
      />
      {error && <div className="wf-form-error">{error}</div>}
    </>
  );
}

function fieldComponent(name, field, value, onChange, required) {
  const type = field?.type;
  if (type === 'string' || type === undefined) {
    return <StringField name={name} field={field || {}} value={value} onChange={onChange} required={required} />;
  }
  if (type === 'number' || type === 'integer') {
    return <NumberField name={name} field={field} value={value} onChange={onChange} required={required} />;
  }
  if (type === 'boolean') {
    return <BooleanField name={name} value={value} onChange={onChange} />;
  }
  if (type === 'object') {
    return <ObjectField name={name} field={field || {}} value={value} onChange={onChange} required={required} />;
  }
  console.warn(`[DynamicInputsForm] Unsupported field type "${type}" for "${name}". Skipping.`);
  return (
    <div className="wf-form-unsupported">
      Unsupported field type <code>{type}</code> — provide manually in JSON.
    </div>
  );
}

export default function DynamicInputsForm({
  schema,
  values,
  onChange,
  onSubmit,
  submitLabel = 'Start Workflow Run',
  submitting = false,
  submitError = null,
}) {
  // Defensive: V2 may send inputs_schema or schema directly.
  const root = schema?.properties ? schema : schema?.inputs_schema || schema;
  const properties = root?.properties || {};
  const required = Array.isArray(root?.required) ? root.required : [];
  const fieldNames = Object.keys(properties);

  const isComplete = required.every((name) => {
    const v = values?.[name];
    return v !== undefined && v !== null && v !== '';
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isComplete || submitting) return;
    onSubmit?.(values);
  };

  if (fieldNames.length === 0) {
    return (
      <form onSubmit={handleSubmit} className="wf-form">
        <p className="wf-form-empty">
          This workflow takes no inputs. Click below to start a run.
        </p>
        <button
          type="submit"
          className="wf-btn wf-btn-primary"
          disabled={submitting}
        >
          {submitting ? 'Starting…' : submitLabel}
        </button>
        {submitError && <div className="wf-form-error">{submitError}</div>}
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="wf-form">
      {fieldNames.map((name) => {
        const field = properties[name] || {};
        const isRequired = required.includes(name);
        return (
          <div key={name} className="wf-form-field">
            <label htmlFor={`wf-input-${name}`} className="wf-form-label">
              {field.title || name}
              {isRequired && <span className="wf-form-required" aria-hidden="true"> *</span>}
            </label>
            {field.description && (
              <p className="wf-form-help">{field.description}</p>
            )}
            {fieldComponent(name, field, values?.[name], onChange, isRequired)}
          </div>
        );
      })}

      <div className="wf-form-actions">
        <button
          type="submit"
          className="wf-btn wf-btn-primary"
          disabled={!isComplete || submitting}
        >
          {submitting ? 'Starting…' : submitLabel}
        </button>
        {!isComplete && (
          <span className="wf-form-hint">
            Fill in all required fields to start a run.
          </span>
        )}
      </div>

      {submitError && <div className="wf-form-error">{submitError}</div>}
    </form>
  );
}
